import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDeepCompareEffect } from 'react-use'

import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'

import HFTextInput from 'components/HFInputs/HFTextInput'
import HFOptionGroup from 'components/HFInputs/HFOptionGroup'
import HFDropdown from 'components/HFInputs/HFDropdown'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'

import { useFetchList } from 'store'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, FormProvider } from 'react-hook-form'
import {
	Appointment,
	AppointmentProtocolRule,
	ClinicalProcess,
	MedicalActProtocolRule,
	Options,
	ProcessForwarding,
	Requisition,
} from 'models'
import { triageOpinionSchema } from './TriageOpinionSchema'

import FinalizeTaskDialog from 'controllers/TaskManager/controllers/FinalizeTaskController/FinalizeTaskDialog'
import TaskHeaderErrors from 'components/TaskHeaderErrors/TaskHeaderErrors'
import useSubmitTask from 'hooks/useSubmitTask'

import styles from '../../Dialog.module.sass'
import ExpandableWidget from 'components/ExpandableWidget/ExpandableWidget'
import TaskNotesController from 'controllers/TaskManager/TaskSubControllers/TaskNotesController/TaskNotesController'

import TaskHeaderController from 'controllers/TaskManager/TaskSubControllers/TaskHeader/TaskHeader'
import AlertBox from 'components/AlertBox/AlertBox'
import TaskMenuController from '../../TaskMenu/TaskMenuController'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import { InsuranceEntity, Provider, Segment } from 'utils/enums'
import HFBodyPartInjury from '../../../../../components/HFInputs/HFBodyPartInjury'
import MedicalOpinion from 'models/MedicalOpinion'
import { useReload } from 'store'
import HFAppointmentProtocol from 'components/HFInputs/HFAppointmentProtocol'

import protocolMedicalActs from '../../../../../dialogs/DataEditingDialogs/EditProcessProtocolDialog/ProtocolMedicalActs.json'
import HFMedicalActsProtocol from 'components/HFInputs/HFMedicalActsProtocol'
import useSession from 'hooks/useSession'
import {
	APPOINTMENT_PROTOCOLS,
	MEDICAL_ACT_PROTOCOLS,
} from 'dialogs/DataEditingDialogs/EditProcessProtocolDialog/EditProcessProtocolDialog'
import { useNoWaitFetchList } from 'store'
import { taskNote } from 'store/atoms'
import { useSetRecoilState } from 'recoil'
import { API_URL, fetchOptions } from 'store/fetchUtils'
import HFCheckbox from '../../../../../components/HFInputs/HFCheckbox'

const validateFields = (value) => {
	if (value !== undefined && value && value.length) {
		return 'valid'
	}
	return 'missing'
}

const TriageOpinionTaskController = (props) => {
	const { i18n } = useGlobal()
	const { dismiss, id, resolve } = props
	const { data } = props
	const reload = useReload()
	const [defaultValues, setDefaultValues] = useState(null)
	const { user: sessionUser } = useSession()
	const postalCode = data?.injured.postalCode
	const accidentType = data?.segment.id
	const setNoteValue = useSetRecoilState(taskNote)
	const isAdministrative = sessionUser?.roles.includes('manager')
	const [suggestionsLog, setSuggestionsLog] = useState({})
	const validateRef = useRef({})
	const [showWarning, setShowWarning] = useState(false)
	const [blockCount, setBlockCount] = useState(0)

	const confirmationOptions = [
		{
			value: 'valid',
			text: i18n`Yes`,
		},
		{
			value: 'invalid',
			text: i18n`No`,
		},
		{
			value: 'missing',
			text: i18n`Missing`,
		},
	]

	const booleanOptions = [
		{
			value: true,
			text: i18n`Yes`,
		},
		{
			value: false,
			text: i18n`No`,
		},
	]

	const processComplexityOptions = [
		{
			value: 'normal',
			text: i18n`Simple`,
		},
		{
			value: 'complex',
			text: i18n`Potentially complex`,
		},
	]

	const processDurationOptions = [
		{
			value: 3,
			text: i18n`Very short`,
		},
		{
			value: 1,
			text: i18n`Short`,
		},
		{
			value: 2,
			text: i18n`Long`,
		},
		{
			value: 4,
			text: i18n`Very long`,
		},
	]

	const validateOptions = [
		{
			value: 'valid',
			text: i18n`Yes`,
		},
		{
			value: 'invalid',
			text: i18n`No`,
		},
		{
			value: 'missing',
			text: i18n`Dubious`,
		},
	]

	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(triageOpinionSchema),
		defaultValues: {
			process: {
				bodyParts: [
					{
						bodyPartId: '',
						injuryId: '',
						primary: false,
						lateralityId: '',
					},
				],
				/*		transferActions: {
					requestOpinion: false,
					notifyManagement: false,
					insertTransferRequest: false,
				},
				transferOpinion: {
					medicalOpinionId: '4',
				},

		 */
			},
			appointmentsProtocol: [],
			medicalActsProtocol: [],
		},
	})

	const { handleSubmit, watch, reset, formState, getValues, setValue } =
		methods
	const { error, submitting, approveTask } = useSubmitTask(
		data?.id,
		formState?.errors
	)
	const [
		mechanismType,
		diagnosis,
		bodyPartsForm,
		acutePhase,
		transfer,
		transferActions,
	] = watch([
		'process.injuryMechanism.mechanismType',
		'process.injuryMechanism.diagnosis',
		'process.bodyParts',
		'process.acutePhase',
		'process.transfer',
		'process.transferActions',
	])

	useEffect(() => {
		if (!['valid', 'missing'].includes(mechanismType)) {
			setValue('process.injuryMechanism.diagnosis', undefined)
			setValue('process.injuryMechanism.causalLink', undefined)
		} else if (!diagnosis) {
			setValue('process.injuryMechanism.causalLink', undefined)
		}
	}, [mechanismType, diagnosis, setValue])

	const bodyPartsString = (bodyPartsForm || [])
		.map((item) => item.bodyPartId)
		.join(',')
	const injuriesString = (bodyPartsForm || [])
		.map((item) => item.injuryId)
		.join(',')

	const {
		contents: [
			providerEntities,
			clinicalProcess,
			appointments,
			requisitions,
			bodyParts,
			injuries,
			latestForwardingTask,
			latestTriage,
			processForward,
			medicalSpecialties,
			medicalActs,
			appointmentProcessProtocols,
			medicalActProcessProtocols,
			professions,
			medicalOpinionTypes,
			fetchedOpinionUsers,
			medicalSpecialtiesOptions,
		],
		state,
	} = useFetchList(
		[
			`/metadata/provider_entities`,
			`clinical_processes/${data?.processId}`,
			`clinical_processes/${data?.processId}/appointments`,
			`clinical_processes/${data?.processId}/requisitions`,
			`metadata/trust_body_parts`,
			`metadata/injuries_types`,
			`clinical_processes/${data?.processId}/process_forward`,
			`/clinical_processes/${data?.processId}/latest_triage`,
			`/clinical_processes/${data?.processId}/process_forward`,
			'/metadata/medical_specialties',
			'/metadata/medical_acts',
			`/rules/clinical_process/${data?.processId}?ruleType=AutomationRule&taskType=AppointmentClinicalApproval`,
			`/rules/clinical_process/${data?.processId}?ruleType=AutomationRule&taskType=MedicalActsClinicalApproval`,
			`/metadata/professions`,
			`metadata/opinion_types`,
			`metadata/task_eligible_users`,
			`/metadata/medical_specialties`,
		],
		[
			Options.fromSimpleObjectJSON, // provider_entities
			ClinicalProcess.fromJSON, // clinical_processes
			Appointment.fromJSON, // appointments
			Requisition.fromJSON, // requisitions
			Options.fromSimpleObjectJSON, // trust_body_parts
			Options.fromSimpleObjectJSON, // injuries_types
			ProcessForwarding.fromJSON, // process_forward
			MedicalOpinion.fromJSON, // latest_triage
			ProcessForwarding.fromJSON, // process_forward
			Options.fromSimpleObjectJSON, // medical_specialties
			Options.fromSimpleObjectJSON, // medical_acts
			null, // appointmentProcessProtocols (sem parser)
			null, // medicalActProcessProtocols (sem parser)
			Options.fromSimpleObjectJSON, // professions
			(json) => ({
				// opinion_types
				...Options.fromSimpleObjectJSON({
					id: json.id,
					name: json.opinionType,
				}),
			}),
			(json) => ({
				// task_eligible_users
				value: `${json.id}`,
				text: i18n.translate(json.name),
				roles: json.roles,
			}),
			(json) => ({
				value: `${json.id}`,
				text: i18n.translate(json.name),
			}),
		]
	)

	const opinionUsers = fetchedOpinionUsers?.filter(
		(user) => !user?.roles?.includes('doctor')
	)
	console.log('medicalOpinionTypes', medicalOpinionTypes) // <- aqui

	const { contents } = useNoWaitFetchList(
		[`/tasks/${data?.id}/notes`],
		[
			(json) => {
				return json.notes
			},
		]
	)

	const [notesContents] = contents

	const { contents: notes, state: notesState } =
		notesContents && notesContents.state === 'hasValue'
			? notesContents
			: { state: notesContents?.state }

	const isNotesLoading = notesState === 'loading'

	//console.log('🚀 ~ TriageOpinionTaskController ~ notes:', notes)

	//console.log('🚀 ~ TriageOpinionTaskController ~ isNotesLoading:', isNotesLoading)

	useEffect(() => {
		if (notes) {
			setNoteValue(notes)
		} else {
			setNoteValue('')
		}
	}, [notes, setNoteValue])

	const professionsOptions = professions?.map((p) => ({
		value: p.id,
		text: p.name,
	}))

	const {
		contents: [presetProtocols],
		state: presetProtocolsState,
	} = useFetchList(
		[
			isAdministrative &&
			data?.segment?.abbreviation === Segment.WorkRelated &&
			(APPOINTMENT_PROTOCOLS || MEDICAL_ACT_PROTOCOLS) &&
			bodyPartsString &&
			injuriesString
				? `/rules/protocols_presets/triage/body_part/${bodyPartsString}/injury/${injuriesString}/acute_phase/${
						acutePhase ?? false
				  }`
				: null,
		].filter(Boolean),
		[]
	)

	const isLoading = state === 'loading'

	const availableProtocolMedicalActs = new Set(protocolMedicalActs)

	const availableProtocolMedicalActsOptions = medicalActs?.filter(
		({ text: ma }) => availableProtocolMedicalActs.has(ma.split(' ')?.[0])
	)

	const isAGEASorOcidentalInsurance = [
		InsuranceEntity.Ageas,
		InsuranceEntity.Ocidental,
	].includes(data?.insuranceCompany?.id)
	const isForwardedByTrust = !!latestForwardingTask?.contactAttempts?.find(
		(item) => item.success
	)
	const isLisboaSAMSorPortoHSMProvider = [
		Provider.LisboaSAMS,
		Provider.PortoHSM,
	].includes(data?.provider?.id)

	const isFollowUpTriage = data?.subType === 'FollowUpTriageOpinion'
	const mapDurationToId = (duration) => {
		const map = {
			'Muito Curto': 3,
			Curto: 1,
			Longo: 2,
			'Muito Longo': 4,
		}
		return map[duration] ?? processDurationOptions[0].value
	}

	const buildInjuryMechanism = (latestTriage) => {
		const mechanismMap = {
			Sim: 'valid',
			Não: 'invalid',
			Duvidoso: 'missing',
		}
		const mappedMechanism = mechanismMap[latestTriage?.mechanismType]

		if (!mappedMechanism || mappedMechanism === 'invalid') {
			return { mechanismType: mappedMechanism ?? undefined }
		}

		const diagnosis = latestTriage?.isDiagnosisTriage === 'Sim'

		if (!diagnosis) {
			return { mechanismType: mappedMechanism }
		}

		return { mechanismType: mappedMechanism, diagnosis: true }
	}

	const priorities = useMemo(
		() => [
			{ value: 1, text: i18n`Very High` },
			{ value: 2, text: i18n`High` },
			{ value: 3, text: i18n`Medium` },
			{ value: 4, text: i18n`Low` },
			{ value: 5, text: i18n`Very Low` },
		],
		[i18n]
	)

	useEffect(() => {
		if (
			!isLoading &&
			defaultValues &&
			!formState.isDirty &&
			formState.submitCount === 0
		) {
			const resetValues = { ...defaultValues }

			//NOTE: Due to old form state with only one body part
			Reflect.deleteProperty(resetValues.process, 'bodyPartId')
			Reflect.deleteProperty(resetValues.process, 'injuryId')
			Reflect.deleteProperty(resetValues, 'injured.name')
			Reflect.deleteProperty(resetValues, 'injured.birthday')
			Reflect.deleteProperty(resetValues, 'injured.occupation')
			Reflect.deleteProperty(resetValues, 'accident.date')
			Reflect.deleteProperty(resetValues, 'accident.description')
			Reflect.deleteProperty(resetValues, 'process.complexity')
			Reflect.deleteProperty(resetValues, 'process.transfer')
			Reflect.deleteProperty(resetValues, 'process.duration')
			Reflect.deleteProperty(resetValues, 'process.hasPreviousSurgery')
			Reflect.deleteProperty(resetValues, 'process.biologicRisk')
			Reflect.deleteProperty(resetValues, 'process.bodyParts')

			reset &&
				reset({
					...resetValues,
					process: {
						...resetValues.process,
						bodyParts: resetValues.process.bodyParts ?? [
							{
								bodyPartId: '',
								injuryId: '',
								primary: false,
								lateralityId: '',
							},
						],
					},
					appointmentsProtocol: [],
					medicalActsProtocol: [],
				})

			setTimeout(() => {
				setValue(
					'process.bodyParts',
					resetValues.process.bodyParts ?? [
						{
							bodyPartId: '',
							injuryId: '',
							primary: false,
							lateralityId: '',
						},
					],
					{ shouldDirty: true, shouldTouch: true }
				)

				/* CM-233 */
				if (resetValues.appointmentsProtocol?.length) {
					setValue(
						'appointmentsProtocol',
						resetValues.appointmentsProtocol,
						{
							shouldDirty: true,
							shouldTouch: true,
						}
					)
				}
				if (resetValues.medicalActsProtocol?.length) {
					setValue(
						'medicalActsProtocol',
						resetValues.medicalActsProtocol,
						{
							shouldDirty: true,
							shouldTouch: true,
						}
					)
				}
			}, 100)
		}
	}, [
		reset,
		defaultValues,
		isLoading,
		formState.isDirty,
		setValue,
		formState.submitCount,
	])

	useDeepCompareEffect(() => {
		let defaultValues = {}

		if (!isLoading && formState.submitCount === 0) {
			if (clinicalProcess) {
				const occupationValue =
					professionsOptions?.find(
						(p) => p.text === clinicalProcess?.injured?.profession
					)?.text || ''

				const values = {
					injured: {
						name: validateFields(clinicalProcess?.injured?.name),
						birthday: validateFields(
							clinicalProcess?.injured?.birthday
						),
						occupation: validateFields(occupationValue),
					},
					accident: {
						date: validateFields(
							`${clinicalProcess?.accident?.date}`
						),
						description: validateFields(
							clinicalProcess?.accident?.description
						),
					},
					process: {
						complexity: isFollowUpTriage
							? latestTriage?.rawTriageValues?.complexity ===
							  'PPC'
								? 'complex'
								: 'normal'
							: 'normal',
						transfer: isFollowUpTriage
							? !!latestTriage?.rawTriageValues?.canTransfer
							: false,
						transferActions: {
							requestOpinion: false,
							notifyManagement: false,
							insertTransferRequest: false,
						},
						/*CM 197 -> Medium priority as default */
						transferOpinion: {
							medicalOpinionId: '4',
							priority: 3,
						},
						duration: isFollowUpTriage
							? mapDurationToId(
									latestTriage?.rawTriageValues?.duration
							  )
							: processDurationOptions[0].value,
						hasPreviousSurgery: isFollowUpTriage
							? !!latestTriage?.rawTriageValues
									?.hasPreviousSurgery
							: false,
						biologicRisk: isFollowUpTriage
							? !!latestTriage?.rawTriageValues?.biologicRisk
							: false,
						acutePhase: latestTriage?.acutePhase ?? undefined,
						bodyParts: latestTriage?.bodyParts
							? latestTriage?.bodyParts.map((item, index) => {
									return {
										id: index.toString(),
										bodyPartId: `${item?.trustBodyPart.id}`,
										injuryId: `${item?.injuryType.id}`,
										primary: item?.isMainBodyPart,
										lateralityId: `${item?.laterality}`,
									}
							  })
							: [
									{
										bodyPartId: '',
										injuryId: '',
										primary: false,
										lateralityId: '',
									},
							  ],
						/* CM -209 */
						injuryMechanism:
							isFollowUpTriage &&
							!(
								isAGEASorOcidentalInsurance &&
								isLisboaSAMSorPortoHSMProvider
							)
								? buildInjuryMechanism(latestTriage)
								: undefined,
					},
					appointmentsProtocol: [],
				}

				const parsedFormState = data.formState
					? typeof data.formState === 'string'
						? JSON.parse(data.formState)
						: data.formState
					: null
				const result = triageOpinionSchema.safeParse(parsedFormState)

				defaultValues =
					!isFollowUpTriage && data.formState && result.success
						? data.formState
						: values
			}

			if (appointmentProcessProtocols?.length) {
				const appointmentsRuleValuesObj =
					AppointmentProtocolRule.retrieveRuleValues(
						appointmentProcessProtocols
					)

				defaultValues = {
					...defaultValues,
					appointmentsProtocol: appointmentsRuleValuesObj,
				}
			}

			if (medicalActProcessProtocols?.length) {
				const medicalActsRuleValuesObj =
					MedicalActProtocolRule.retrieveRuleValues(
						medicalActProcessProtocols
					)

				defaultValues = {
					...defaultValues,
					medicalActsProtocol: medicalActsRuleValuesObj,
				}
			}

			setDefaultValues(defaultValues)
		}
	}, [isLoading, clinicalProcess ?? {}, formState.submitCount])

	useEffect(() => {
		const presetAppointmentProtocols = presetProtocols?.appointmentProtocols
		const presetMedicalActsProtocols = presetProtocols?.medicalActsProtocols

		if (presetProtocolsState !== 'loading') {
			if (!appointmentProcessProtocols?.length) {
				setValue(
					'appointmentsProtocol',
					AppointmentProtocolRule.retrieveRuleValues(
						presetAppointmentProtocols || []
					)
				)
			}

			if (!medicalActProcessProtocols?.length) {
				setValue(
					'medicalActsProtocol',
					MedicalActProtocolRule.retrieveRuleValues(
						presetMedicalActsProtocols || []
					)
				)
			}
		}
		// eslint-disable-next-line
	}, [presetProtocolsState, presetProtocols])

	const onSubmit = async (formData) => {
		const hasBlocked = Object.values(validateRef.current).some(Boolean)
		if (hasBlocked) {
			setShowWarning(true)
			return
		}

		let finalData = {}

		const {
			process,
			appointmentsProtocol,
			medicalActsProtocol,
			...remainingData
		} = formData
		const { bodyParts, ...remainingProcessData } = process

		const filteredAppointments = appointmentsProtocol?.filter(
			({ specialtyId }) => specialtyId !== ''
		)

		const appointmentsRequestBody =
			AppointmentProtocolRule.constructRequestBody(
				filteredAppointments,
				`Automação protocolo para processo: ${data?.processId}`
			)

		const filteredMedicalActs = medicalActsProtocol?.filter(
			({ medicalActId }) => medicalActId !== ''
		)

		const medicalActsRequestBody =
			MedicalActProtocolRule.constructRequestBody(
				filteredMedicalActs,
				`Automação protocolo para processo: ${data?.processId}`
			)

		finalData = {
			...remainingData,
			notes: formData.notes,
			appointmentsProtocol: appointmentsRequestBody,
			medicalActsProtocol: medicalActsRequestBody,
			process: {
				...remainingProcessData,
				transferActions: process.transferActions,
				transferOpinion: process.transferActions?.requestOpinion
					? process.transferOpinion
					: undefined,
			},
		}

		if (isForwardedByTrust) {
			finalData = {
				...finalData,
				injured: {
					name: 'valid',
					birthday: 'valid',
					occupation: 'valid',
				},
				accident: {
					date: 'valid',
					description: 'valid',
				},
				process: {
					...finalData.process,
					hasPreviousSurgery:
						!!processForward?.medicalAssistanceInfo
							?.hadPreviousSurgeries,
				},
			}
		}

		if (bodyParts && bodyParts.length) {
			const mappedBodyParts = bodyParts.flatMap(
				({ bodyPartId, injuryId, primary, lateralityId }) => {
					if (lateralityId === 4) {
						return [
							{
								bodyPartId,
								injuryId,
								primary,
								lateralityId: 1,
							},
							{
								bodyPartId,
								injuryId,
								primary,
								lateralityId: 2,
							},
						]
					}
					return [{ bodyPartId, injuryId, primary, lateralityId }]
				}
			)

			finalData = {
				...finalData,
				process: {
					...finalData.process,
					bodyParts: mappedBodyParts,
				},
			}
		}

		try {
			console.log('finalData', JSON.stringify(finalData, null, 2))

			if (
				process.transferActions?.insertTransferRequest &&
				process.transferRequest?.specialty
			) {
				try {
					await fetch(
						`${API_URL}/api/clinical_processes/${data?.processId}/transfers`,
						fetchOptions({
							method: 'POST',
							body: {
								idEspecialidade: Number(
									process.transferRequest.specialty
								),
								motivo: process.transferRequest.reason,
							},
						})
					)
				} catch (error) {
					console.error('Erro ao submeter transferência:', error)
				}
			}

			await approveTask(finalData, 'submit')
			/*CM-200 reload processoTag */
			reload([
				`clinical_processes/${data?.processId}`,
				`/clinical_processes/${data?.processId}`,
				`/clinical_processes/${data?.processId}/latest_triage`,
				`/rules/clinical_process/${data?.processId}?ruleType=AutomationRule&taskType=AppointmentClinicalApproval`,
				`/rules/clinical_process/${data?.processId}?ruleType=AutomationRule&taskType=MedicalActsClinicalApproval`,
				'/tasks/me?state=Created',
				'/tasks/me?state=Postponed',
				'/tasks/stats',
			])

			const logEntries = filteredMedicalActs
				.map((act) => {
					const suggestion = suggestionsLog[String(act.medicalActId)]
					if (!suggestion?.response?.providerReferrals?.length)
						return null
					return {
						...suggestion,
						providerId: act.serviceProviderId || null,
					}
				})
				.filter(Boolean)

			for (const act of logEntries) {
				try {
					const logRequestBodyWithoutRequest = {
						processId: data?.processId,
						requestingProviderId: null,
						requisitionId: null,
						codes: String(act.actId),
						postalCode: postalCode,
						scheduleDate: null,
						appointmentDate: null,
						providers:
							act.response?.providerReferrals?.map((p) => ({
								providerId: p.providerId,
								profitProvider: Math.round(p.profit || 0),
								distance: p.distance || 0,
								speed: p.averageTime || 0,
							})) || [],
						chosenProvider: act.providerId || null,
						username: JSON.parse(localStorage.getItem('user'))
							?.username,
						justification:
							act.userJustification ||
							act.response?.description ||
							'Aprovação manual',
						userJustification: act.userJustification ?? null,
						userJustificationId: act.userJustificationId ?? null,
						suggestType: act.suggestType ?? null,
					}

					const logRequestBody = {
						...logRequestBodyWithoutRequest,
						request: JSON.stringify(logRequestBodyWithoutRequest),
					}

					await fetch(`${API_URL}/api/suggest/logs`, {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify(logRequestBody),
					})
				} catch (error) {
					console.error(
						`Erro ao guardar log para ${act.actId}:`,
						error
					)
				}
			}

			resolve()
		} catch (error) {
			console.error('Erro ao guardar:', error)
		}
	}

	if (isLoading && !formState.isDirty) {
		return <LoadingIndicator />
	}

	const customError = error?.map((err) => {
		return {
			title: i18n`Error`,
			text: err,
		}
	})

	const birthdayDate = clinicalProcess?.injured?.birthdayInfo?.birthdayDate
	const injuredBirthday = birthdayDate
		? (() => {
				const [day, month, year] = birthdayDate.split('/')
				return `${year}-${month}-${day}`
		  })()
		: null
	const injuredType = bodyPartsForm?.[0]?.injuryId
	const bodyPart = bodyPartsForm?.[0]?.bodyPartId

	const getMinDatetime = () => {
		const now = new Date()
		const year = now.getFullYear()
		const month = String(now.getMonth() + 1).padStart(2, '0')
		const day = String(now.getDate()).padStart(2, '0')
		const hours = String(now.getHours()).padStart(2, '0')
		const minutes = String(now.getMinutes()).padStart(2, '0')
		return `${year}-${month}-${day}T${hours}:${minutes}`
	}

	return (
		<>
			<TaskHeaderErrors errors={error ?? []} />
			{submitting ? (
				<FinalizeTaskDialog
					dismiss={dismiss}
					submitting={submitting}
					task={data}
				/>
			) : (
				<>
					<FormProvider {...methods}>
						<div className={styles['form-container']}>
							<TaskHeaderController task={data} />
							<div className={styles['notes-form-container']}>
								<TaskNotesController
									data={{
										id: data.id,
										processId: data.processId,
										taskNote: notes,
									}}
									isNotesLoading={isNotesLoading}
								/>
							</div>
							<form
								id={`form_${id}`}
								className={styles['form']}
								onSubmit={handleSubmit(onSubmit)}
							>
								{customError && customError.length > 0 && (
									<AlertBox
										className={styles['alert-box']}
										type='error'
										items={customError}
									/>
								)}

								{!isForwardedByTrust && (
									<>
										<fieldset style={{ display: 'none' }}>
											<h4>
												{i18n`Confirmation of process data`}
											</h4>

											<VStack
												className={
													styles['option-group-list']
												}
											>
												<HFOptionGroup
													className={`${styles['option-group']} ${styles['--horizontal-flex']}`}
													name='injured.name'
													label={i18n`Name: ${clinicalProcess?.injured?.name}`}
													options={
														confirmationOptions
													}
												/>
												{watch('injured.name') ===
													'invalid' && (
													<>
														<h5>
															Nome fornecido pelo
															Sinistrado
														</h5>
														<HFTextInput name='injured.providedName' />
													</>
												)}

												<HFOptionGroup
													className={`${styles['option-group']} ${styles['--horizontal-flex']}`}
													name='injured.birthday'
													label={i18n`Birthday: ${clinicalProcess?.injured?.birthday}`}
													options={
														confirmationOptions
													}
												/>
												{watch('injured.birthday') ===
													'invalid' && (
													<>
														<h5>
															Data de Nascimento
															fornecida pelo
															Sinistrado
														</h5>

														<HFTextInput
															name='injured.providedBirthday'
															type='date'
														/>
													</>
												)}

												<HFOptionGroup
													className={`${styles['option-group']} ${styles['--horizontal-flex']}`}
													name='injured.occupation'
													label={i18n`Occupation: ${clinicalProcess?.injured?.profession}`}
													options={
														confirmationOptions
													}
												/>
												{watch('injured.occupation') ===
													'invalid' && (
													<>
														<h5>
															Profissão fornecida
															pelo Sinistrado
														</h5>
														<HFDropdown
															name='injured.providedOccupation'
															options={
																professionsOptions
															}
															searchable={true}
															virtualized={true}
															optionsAlign='left'
															noData={i18n`No results`}
														/>
													</>
												)}

												<HFOptionGroup
													className={`${styles['option-group']} ${styles['--horizontal-flex']}`}
													name='accident.date'
													label={i18n`Accident date: ${clinicalProcess?.accident?.date?.shortDateFormat}`}
													options={
														confirmationOptions
													}
												/>
												{watch('accident.date') ===
													'invalid' && (
													<>
														<h5>
															Data do Acidente
															fornecida pelo
															sinistrado
														</h5>

														<HFTextInput
															name='accident.providedDate'
															type='date'
														/>
													</>
												)}

												<HFOptionGroup
													className={`${styles['option-group']} ${styles['--horizontal-flex']}`}
													name='accident.description'
													label={i18n`Is there accident description?`}
													options={
														confirmationOptions
													}
												/>
												{watch(
													'accident.description'
												) === 'invalid' && (
													<>
														<h5>
															Descrição do
															Acidente fornecida
															pelo Sinistrado
														</h5>
														<HFTextInput name='accident.providedDescription' />
													</>
												)}
											</VStack>
										</fieldset>

										<fieldset>
											<h4>{i18n`Surgery`}</h4>
											<HFOptionGroup
												className={`${styles['option-group']} ${styles['--horizontal-flex']}`}
												name='process.hasPreviousSurgery'
												type='boolean'
												label={i18n`Has any surgery been performed previously?`}
												options={booleanOptions}
											/>
										</fieldset>
									</>
								)}

								<fieldset>
									<VStack
										className={styles['fieldset-div-list']}
									>
										<h4>{i18n`Injury and body part`}</h4>
										<HFBodyPartInjury
											name='process.bodyParts'
											data={{ bodyParts, injuries }}
										/>
										<HFOptionGroup
											className={`${styles['option-group']} ${styles['--horizontal-flex']}`}
											name='process.biologicRisk'
											type='boolean'
											label={i18n`Is there a biological risk?`}
											options={booleanOptions}
										/>
									</VStack>
								</fieldset>

								{!(
									isAGEASorOcidentalInsurance &&
									isLisboaSAMSorPortoHSMProvider
								) && (
									<>
										<fieldset>
											<h4>{i18n`Accident`}</h4>
											<VStack gap='xsmall'>
												<HFOptionGroup
													className={`${styles['option-group']} ${styles['--horizontal-flex']}`}
													name='process.injuryMechanism.mechanismType'
													label={i18n`Is there an injury mechanism that can be characterized as an accident?`}
													options={validateOptions}
												/>
												{['valid', 'missing'].includes(
													mechanismType
												) && (
													<HFOptionGroup
														className={`${styles['option-group']} ${styles['--horizontal-flex']}`}
														name='process.injuryMechanism.diagnosis'
														type='boolean'
														label={i18n`Is there a diagnosis?`}
														options={booleanOptions}
													/>
												)}
												{['valid', 'missing'].includes(
													mechanismType
												) &&
													diagnosis && (
														<HFOptionGroup
															className={`${styles['option-group']} ${styles['--horizontal-flex']}`}
															name='process.injuryMechanism.causalLink'
															label={i18n`Do the diagnoses made have a causal link with the described injury mechanism?`}
															options={
																validateOptions
															}
														/>
													)}
												<HFOptionGroup
													className={`${styles['option-group']} ${styles['--horizontal-flex']}`}
													name='process.acutePhase'
													type='boolean'
													label={i18n`Treatment in acute phase?`}
													options={booleanOptions}
												/>
												{acutePhase === true && (
													<HFTextInput
														name='process.acutePhaseNotificationDate'
														label={i18n`Quando pretende ser notificado?`}
														type='datetime-local'
														min={getMinDatetime()}
													/>
												)}
											</VStack>
										</fieldset>
									</>
								)}

								<fieldset>
									<h4>{i18n`Process related information`}</h4>
									<VStack gap='xsmall'>
										<div>
											<h5>{i18n`Process complexity`}</h5>
											<HFDropdown
												name='process.complexity'
												options={
													processComplexityOptions
												}
												searchable={true}
												optionsAlign='right'
												noData={i18n`No results`}
											/>
										</div>
										{/* <div>
											<h5>{i18n`Process duration`}</h5>
											<HFDropdown
												name='process.duration'
												options={processDurationOptions}
												searchable={true}
												optionsAlign='right'
												noData={i18n`No results`}
											/>
										</div> */}
									</VStack>
								</fieldset>

								<fieldset>
									<HFOptionGroup
										className={`${styles['option-group']} ${styles['--horizontal-flex']}`}
										name='process.transfer'
										type='boolean'
										label={i18n`Transfer process?`}
										options={booleanOptions}
									/>

									{transfer === true && (
										<VStack
											gap='xsmall'
											style={{ marginTop: '12px' }}
										>
											<div
												className={styles['checkbox']}
												style={{
													display: 'flex',
													flexDirection: 'column',
													gap: '8px',
												}}
											>
												{' '}
												<HFCheckbox
													name='process.transferActions.requestOpinion'
													label={i18n`Request Transfer Opinion`}
												/>
												{transferActions?.requestOpinion ===
													true && (
													<fieldset>
														<h5>{i18n`Transfer Opinion Request`}</h5>
														<HFDropdown
															placeholder={i18n`Medical opinion type`}
															name='process.transferOpinion.medicalOpinionId'
															options={
																medicalOpinionTypes ??
																[]
															}
															searchable
															optionsAlign='right'
															noData={i18n`No results`}
															disabled
														/>
														<HFTextInput
															placeholder={i18n`Request description`}
															name='process.transferOpinion.description'
															multiline
															noResize
															rows={3}
															required
														/>
														<HFDropdown
															placeholder={i18n`Priority`}
															name='process.transferOpinion.priority'
															options={priorities}
															searchable
															optionsAlign='right'
															noData={i18n`No results`}
														/>
														<HFDropdown
															placeholder={i18n`Target user`}
															name='process.transferOpinion.targetUser'
															options={
																opinionUsers ??
																[]
															}
															searchable
															optionsAlign='right'
															noData={i18n`No results`}
														/>
													</fieldset>
												)}
												<HFCheckbox
													name='process.transferActions.notifyManagement'
													label={i18n`Notify management`}
												/>
												<HFCheckbox
													name='process.transferActions.insertTransferRequest'
													label={i18n`Insert transfer request in process`}
												/>
												{transferActions?.insertTransferRequest ===
													true && (
													<fieldset>
														<h5>{i18n`Transfer request`}</h5>
														<HFDropdown
															placeholder={i18n`Specialty`}
															name='process.transferRequest.specialty'
															options={
																medicalSpecialtiesOptions ??
																[]
															}
															searchable={true}
															optionsAlign='right'
															noData={i18n`No results`}
														/>
														<HFTextInput
															placeholder={i18n`Reason`}
															name='process.transferRequest.reason'
															multiline
															noResize
															rows={3}
															required
														/>
													</fieldset>
												)}
											</div>
										</VStack>
									)}
								</fieldset>

								{isAdministrative &&
									clinicalProcess?.accident?.segment
										?.abbreviation ===
										Segment.WorkRelated &&
									(APPOINTMENT_PROTOCOLS ||
										MEDICAL_ACT_PROTOCOLS) && (
										<>
											<fieldset>
												<h4>{i18n`Appointments protocols`}</h4>
												<HFAppointmentProtocol
													data={{
														medicalSpecialties,
														appointments,
														processId:
															data?.processId,
													}}
													name={
														'appointmentsProtocol'
													}
												/>
											</fieldset>

											<fieldset>
												<h4>{i18n`Medical acts protocols`}</h4>
												<HFMedicalActsProtocol
													data={{
														insuranceId:
															clinicalProcess
																?.insurancePolicy
																?.insuranceCompany
																?.id,
														medicalActs:
															availableProtocolMedicalActsOptions,
														requisitions,
														processId:
															data?.processId,
														processPostalCode:
															postalCode,
														accidentType:
															accidentType,
														onUpdateFormData:
															() => {},
													}}
													injuredBirthday={
														injuredBirthday
													}
													injuredType={injuredType}
													bodyPart={bodyPart}
													name={'medicalActsProtocol'}
													onSuggestionsChange={(
														log
													) => {
														setSuggestionsLog(log)
													}}
													blockCountDispatcher={(
														delta
													) =>
														setBlockCount(
															(c) => c + delta
														)
													}
													validateRef={validateRef}
													showWarning={showWarning}
													onJustificationChanged={(
														actId,
														justification,
														justificationId
													) => {
														if (!actId) return
														const key =
															String(actId)
														setSuggestionsLog(
															(prev) => {
																if (!prev[key])
																	return prev
																return {
																	...prev,
																	[key]: {
																		...prev[
																			key
																		],
																		userJustification:
																			justification,
																		userJustificationId:
																			justificationId,
																	},
																}
															}
														)
													}}
												/>
											</fieldset>
										</>
									)}

								<fieldset
									className={styles['observation-container']}
								>
									<ExpandableWidget
										className={styles['observation']}
										heading='h5'
										title={i18n`Administrative observations`}
									>
										<HFTextInput
											name='observations'
											placeholder={i18n`Observations`}
											multiline={true}
											rows={5}
											noResize
										/>
									</ExpandableWidget>
								</fieldset>
							</form>
						</div>
					</FormProvider>
					<TaskMenuController
						data={{
							formId: id,
							options: ['postpone', 'forward'],
							submitting,
							submissionType: 'submit',
							getValues,
							disableResolver: false,
						}}
					/>
				</>
			)}
		</>
	)
}

export default TriageOpinionTaskController
